<script setup lang="ts">
import type { EventBodyProps } from '@/types/appTypes'

const props = withDefaults(defineProps<EventBodyProps>(), { title: '' })

const app = useNuxtApp()
const storeIndex = useStoreIndex()
const storeUser = useStoreUser()
const refDialog = ref(null)

const custom = props.item.custom as any

const pageData = {
  sPlace: '', // 場所
  sIrregularHoliday: '', // 本日開館中情報
  aSlide: null, // スライド情報
  sBody: null, // ボディ
  sPeriodLabel: null, // 会期
  sPeriodRemain: null, // 開催中情報
  sViewingTime: null, // 観覧時間の目安
  sVenue: null, // 会場
  sAddress: null, // 住所
  sTell: null, // TEL
  sOpenTime: null, // 開館時間
  sCloseTime: null, // 休館日
  isEventExpired: null,
  parsedTel: {
    number: '',
    href: '',
    label: '',
  },
}

const refData = reactive({ sSlideCaption: null })
const onChangeSlide = (slideNO) => {
  refData.sSlideCaption = pageData.aSlide[slideNO].sCaption
}

const hasSlides = computed(() => Array.isArray(pageData.aSlide) && pageData.aSlide.length > 0)

const onClickMemo = () => {
  const toPath = `/mypage/memo/0/edit?event=${props.item.article_slug}`
  if (storeUser.state.isLogin) {
    navigateTo(toPath)
  } else {
    storeUser.setLoginRedirectPath(toPath)
    navigateTo('/login')
  }
}

// イベントから表示カラム作成
if (!pageData.sPlace) {
  pageData.sPlace = app.$getEventPlace(props.item)
} // 場所
if (!pageData.sIrregularHoliday) {
  pageData.sIrregularHoliday = app.$getEventIrregularHoliday(props.item)
} // 本日開館中情報
if (!pageData.sBody) { // ボディ
  let strBody = app.$getArticleBody(props.item) ?? ''
  if (typeof strBody !== 'string') strBody = String(strBody ?? '')

  if (strBody && (strBody.includes('<p>') || strBody.includes('<br>'))) {
    // タグモード
    strBody = app.$trimEnterTrue(strBody)
    strBody = app.$brToEnter(strBody)
    strBody = strBody.replace(/<\/p>/g, '\n')
    strBody = app.$stripHtmlTag(strBody)
    strBody = strBody.trim()
  }
  pageData.sBody = strBody
  console.log('##EventBody: sBody', pageData.sBody)
}

pageData.sPeriodLabel = app.$getEventPeriodLabel(props.item)
// 会期
if (props.item.day_count_display === 1) {
  pageData.sPeriodRemain = app.$getEventPeriodRemain(props.item)
}
// 開催中情報
pageData.sVenue = app.$getEventVenue(props.item, false)
pageData.isEventExpired = app.$isEventExpired(props.item)
// 会場
pageData.sAddress = app.$getEventAddress(props.item, true)
// 住所
pageData.sTell = app.$getEventTell(props.item)
// 電話番号
if (custom) {
  // 観覧時間の目安取得
  if (!pageData.sViewingTime && custom.viewing_time) {
    pageData.sViewingTime = app.$getDefineNameByID(storeIndex.state.master.viewing_time.details, custom.viewing_time)
  }

  // 開館時間
  if (!pageData.sOpenTime) {
    if (custom.event_opening_hour) {
      pageData.sOpenTime = custom.event_opening_hour
    } else if (custom.custom_museum_opening_hour) {
      pageData.sOpenTime = custom.custom_museum_opening_hour
    }
  }

  if (!pageData.sCloseTime) {
    if (custom.museum_close_day) {
      pageData.sCloseTime = custom.museum_close_day
    } else if (custom.custom_museum_closed_period) {
      pageData.sCloseTime = custom.custom_museum_closed_period
    }
  }
}

// イベントからスライド作成
if (import.meta.client === true) {
  if (props.item && !pageData.aSlide) {
    if (custom && props.objects) { // 追加
      pageData.aSlide = []
      app.$addSlides(
        props.item,
        'event_slide',
        10,
        props.objects,
        pageData.aSlide,
        (props.item.is_old === 1) ? null : 'l',
      )
    }
    if (props.item.eye_catch_image_object) {
      app.$initSlide(props.item.eye_catch_image_object, pageData.aSlide)
    }
  }
  if (pageData.aSlide?.length === 1) {
    onChangeSlide(0)
  }
}

const setParsedTel = () => {
  const raw = pageData.sTell || ''
  const match = raw.match(/[\d-]+/)

  pageData.parsedTel.number = match ? match[0] : ''
  pageData.parsedTel.href = match ? match[0].replace(/-/g, '') : ''
  pageData.parsedTel.label = match ? raw.replace(match[0], '') : ''
}
setParsedTel()
</script>

<template>
  <div>
    <div class="c-header">
      <div>
        <p
          v-if="custom"
          class="c-header_subtitle"
        >
          {{ custom.event_subtitle }}
        </p>
        <h1
          v-if="item && item.title"
          class="c-header_title"
        >
          {{ item.title }}
          <span
            v-if="custom?.is_event === '1'"
            class="c-header_title_label"
          >
            イベント
          </span>
        </h1>
        <p
          v-if="custom"
          class="c-header_subtitle2"
        >
          {{ custom.event_subtitle2 }}
        </p>
        <p
          v-if="pageData.sPlace"
          class="c-header_location"
        >
          {{ pageData.sPlace }}
        </p>
      </div>
    </div>

    <div
      v-if="pageData.sIrregularHoliday"
      class="c-irregularHoliday"
    >
      <div
        class="c-irregularHoliday_text"
        v-html="pageData.sIrregularHoliday"
      />
    </div>

    <div
      class="c-slide_parent"
    >
      <client-only>
        <div
          v-if="hasSlides"
          class="c-slide"
        >
          <v-carousel
            class="IMCustomCarousel2"
            hide-delimiter-background
            v-bind:show-arrows="pageData.aSlide?.length > 1 ? 'hover' : false"
            v-bind:height="app.$device.isMobile ? '280' : '347'"
            cycle
            v-on:update:model-value="onChangeSlide"
          >
            <v-carousel-item
              v-for="(record, index) in pageData.aSlide"
              v-bind:key="index"
              v-bind:src="record.oImg"
              v-on:click="refDialog.onClickOpen(record.oImg)"
            />
          </v-carousel>
        </div>

        <div
          v-if="refData.sSlideCaption"
          class="c-slide_imgLocation"
        >
          {{ refData.sSlideCaption }}
        </div>
      </client-only>
    </div>

    <div class="c-link">
      <div class="c-link_first">
        <Clip
          class="c-link_first_clip"
          v-bind:mode="true"
          v-bind:dspcnt="true"
          v-bind:article-type-slug="item.article_type_slug"
          v-bind:article-slug="item.article_slug"
          v-bind:count="item.user_favorite_count"
        />
        <div>
          <button
            class="c-link_first_comment"
            v-on:click="onClickMemo"
          >
            <img
              class="c-link_first_comment_img"
              src="@/assets/images/icon-edit-msg2.svg"
            >
            感想を書く
          </button>
        </div>
      </div>
      <ShareButtons
        v-bind:title="props.title"
      />
    </div>

    <div class="c-body">
      <div
        v-if="pageData.sBody"
        class="c-body_text l-blank-icon-none"
      >
        {{ pageData.sBody }}
      </div>
    </div>

    <div class="c-info">
      <table>
        <tbody>
          <tr
            v-if="pageData.sPeriodLabel || pageData.sPeriodRemain"
          >
            <td>
              会期
            </td>
            <td>
              <div v-if="pageData.sPeriodLabel">
                {{ pageData.sPeriodLabel }}
              </div>

              <div
                v-if="pageData.sPeriodRemain"
                class="c-info_timeRemain"
              >
                <div
                  class="c-info_timeRemain_text"
                  v-bind:style="{ color: pageData.sPeriodRemain.color }"
                >
                  {{ pageData.sPeriodRemain.label }}
                </div>
              </div>
              <div
                v-if="item.article_do_not_link && pageData.isEventExpired"
                class="c-info_link"
              >
                <ArrowLink
                  v-bind:to="{ name: 'article-slug', params: { article: 'museum', slug: item.custom.museum_article_slug } }"
                  label="このミュージアムで開催中の展覧会をみる"
                  bold
                />
              </div>
            </td>
          </tr>
          <tr
            v-if="pageData.sOpenTime"
          >
            <td>開館時間</td>
            <td>
              <div v-html="app.$trimEnter(pageData.sOpenTime)" />
            </td>
          </tr>

          <tr
            v-if="custom?.event_admission_fee"
          >
            <td>料金</td>
            <td>
              <div v-html="app.$trimEnter(custom.event_admission_fee)" />
            </td>
          </tr>

          <tr
            v-if="pageData.sCloseTime"
          >
            <td>休館日</td>
            <td>
              <div v-html="app.$trimEnter(pageData.sCloseTime)" />
            </td>
          </tr>

          <tr
            v-if="custom?.ticket_url"
          >
            <td>チケットを買う</td>
            <td>
              <a
                v-bind:href="custom.ticket_url"
                target="_blank"
                class="c-info_web"
              >
                {{ custom.ticket_url }}
              </a>
            </td>
          </tr>
          <tr
            v-if="pageData.sViewingTime"
          >
            <td>観覧時間の目安</td>
            <td>
              {{ pageData.sViewingTime }}
            </td>
          </tr>
          <tr
            v-if="custom?.event_url"
          >
            <td>公式サイト</td>
            <td>
              <a
                v-bind:href="custom.event_url"
                target="_blank"
                class="c-info_web"
              >
                {{ custom.event_url }}
              </a>
            </td>
          </tr>
          <tr
            v-if="pageData.sVenue"
          >
            <td>会場</td>
            <td>
              <div v-html="pageData.sVenue" />
            </td>
          </tr>
          <tr
            v-if="pageData.sAddress || pageData.sTell || (custom?.museum_eye_catch_image_object)"
          >
            <td>住所</td>
            <td>
              <div v-if="pageData.sAddress">
                <div v-html="app.$trimEnter(pageData.sAddress)" />
              </div>

              <div
                v-if="pageData.sTell"
                class="pc-only"
              >
                {{ pageData.sTell }}
              </div>
              <div
                v-if="pageData.parsedTel.number"
                class="sp-only"
              >
                <a
                  v-bind:href="'tel:' + pageData.parsedTel.href"
                >
                  {{ pageData.parsedTel.number }}
                </a>{{ pageData.parsedTel.label }}
              </div>

              <div
                v-if="custom?.museum_eye_catch_image_object && objects"
                class="c-info_image"
              >
                <CommonImage
                  class="c-info_image_item"
                  v-bind:src="storeIndex.getImgInfoByID(custom.museum_eye_catch_image_object, objects)?.url"
                  error-src="/images/noimage.svg"
                  v-bind:alt="item.title"
                  aspect-ratio="3/2"
                  object-fit="contain"
                />
              </div>

              <div
                v-if="custom?.museum_article_slug"
                class="c-info_museumlink"
              >
                <ArrowLink
                  v-if="item.article_do_not_link"
                  v-bind:to="{ path: `/museum/${custom.museum_article_slug}` }"
                  label="ミュージアムの詳細をみる"
                  bold
                />
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <DialogImg
      ref="refDialog"
    />
  </div>
</template>

<style lang="scss" scoped>
@use "@/assets/css/foundation/mixin/responsive" as responsive;

.c-header {
  display: flex;
  font-family: var(--g_font_family_NotoSansCJKjp);

  &_subtitle {
    margin-top: 2px;
    margin-bottom: -3px;
    font-size: 14px;
  }

  &_title {
    margin-top: 2px;
    color: var(--color_text_base);
    font-size: 25px;
    font-weight: 400;

    @include responsive.mq_down(sp) {
      margin: 0 0 16px;
      font-size: 20px;
    }

    &_label {
      display: inline-block;
      height: 27px;
      margin-left: 15px;
      padding: 0 8px;
      border: solid 1px var(--color_primary);
      color: var(--color_primary);
      font-size: 14px;
      line-height: 25px;
      vertical-align: middle;
    }
  }

  &_subtitle2 {
    // margin-top: -13px;
    margin-top: 2px;
    font-size: 14px;

    @include responsive.mq_down(sp) {
      margin-top: -16px;
    }
  }

  &_location {
    margin-top: -6px;
    color: var(--color_text_base);
    font-size: 14px;
    font-weight: bold;
  }
}

.c-irregularHoliday {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 45px;
  padding: 1px 17px;
  border: 3px solid var(--color_primary);

  @include responsive.mq_down(sp) {
    padding: 1px 5px;
  }

  &_text {
    color: var(--color_primary) !important;
    font-family: var(--g_font_family_NotoSansCJKjp);
    font-size: 14px;
    line-height: 1.4;
    text-align: center;
  }
}

.c-slide {
  margin-top: 30px;
  background-color: #ebebeb;

  &_item {
    &_img {
      position: relative;
      width: 100%;
      background-color: #ebebeb;

      @include responsive.mq_down(sp) {
        height: 48vw;
      }
    }
  }

  &_imgLocation {
    display: flex;

    // justify-content: flex-end;
    margin-top: 5px;
    color: #7d7d7d;
    font-family: var(--g_font_family_NotoSansCJKjp);
    font-size: 14px;
    line-height: 1.4;
  }
}

.c-body {
  margin-top: 19px;
  color: var(--color_text_base);
  font-family: var(--g_font_family_NotoSansCJKjp);
  font-size: 16px;
  line-height: 1.75;

  &_title {
    font-size: 20px;
    line-height: 1.6;
  }

  &_text {
    // transform: rotate(0.03deg);
    // font-family: var(--g_font_family_MPuls);
    font-family: var(--g_font_family_NotoSansCJKjp);
    font-size: 16px;
    line-height: 1.8;
    word-break: break-all;
    white-space: pre-line;
  }
}

.c-info {
  margin-top: 63px;

  &_web {
    color: var(--color_primary_link);
  }

  &_timeRemain {
    display: flex;
    align-items: center;
  }

  &_image {
    position: relative;
    width: 100%;
    margin-top: 20px;

    // padding-top: 68%;

    &_item {
      // position: absolute;
      top: 0;
      left: 0;
      width: 100%;

      // height: 100%;
      background-color: #ebebeb;
    }
  }

  &_museumlink {
    display: flex;
    justify-content: flex-end;
    margin-top: 7px;
  }

  table {
    width: 100%;
    border-collapse: collapse;
    font-family: var(--g_font_family_NotoSansCJKjp);
    font-size: 14px;

    tbody,
    tr,
    th,
    td {
      @include responsive.mq_down(sp) {
        display: block;
      }
    }

    tr {
      td {
        padding: 9px 20px;
        border: 1px solid #ebebeb;

        @include responsive.mq_down(sp) {
          padding: 6px 10px;
          word-break: break-all;
        }

        &:first-child {
          width: 200px;
          border: 1px solid #ebebeb;
          background: #ebebeb;
          color: #252525;
          font-weight: bold;

          @include responsive.mq_down(sp) {
            width: 100%;
          }
        }

        &:last-child {
          width: calc(100% - 200px);
          line-height: 1.85;

          @include responsive.mq_down(sp) {
            width: 100%;
          }
        }
      }
    }
  }

  &_link {
    .c-arrowLink {
      text-align: left;
    }
  }
}

.c-link {
  display: flex;
  justify-content: center;
  margin-top: 32px;

  @include responsive.mq_down(sp) {
    flex-direction: column;
    align-items: center;
    margin-top: 49px;
  }

  & > div {
    @include responsive.mq_down(sp) {
      margin: 5px 0;
    }

    &:last-of-type {
      @include responsive.mq_down(sp) {
        margin-top: 15px;
      }
    }
  }

  &_first {
    display: flex;
    justify-content: center;

    @include responsive.mq_down(small) {
      flex-direction: column;
      align-items: center;
    }

    &_comment {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 152px;
      height: 40px;
      margin: 0 20px;
      border: 1px solid var(--color_border_control);
      border-radius: 50px;
      font-size: 14px;
      font-weight: bold;
      letter-spacing: normal;

      @include responsive.mq_down(sp) {
        margin: 0 0 0 20px;
      }

      @include responsive.mq_down(small) {
        margin: 20px 0 0;
      }

      &:hover {
        opacity: 0.7;
      }

      &_img {
        width: 21px;
        height: 21px;
        margin-right: 10px;
      }
    }
  }

  &_button {
    display: flex;
    margin: 0 20px;

    &_border {
      min-width: 60px !important;
      min-height: 40px;

      &:hover {
        opacity: 0.7;
      }
    }

    &_middle {
      border-right: 0;
      border-left: 0;
    }
  }
}
</style>
